import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal, Space, Spin, Table, Tag, message } from 'antd';
import type { Member } from '../../types/domain';
import { useAuth } from '../../infrastructure/auth0/useAuth';
import { useMembersData } from '../../hooks/data/useMembersData';

type Props = {
  onSelect: (member: Member) => void;
};

const MemberListTable = ({ onSelect }: Props): ReactNode => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const { isLoading, members, removeMember } = useMembersData();

  const handleClickRemoveMember = (member: Member): void => {
    Modal.confirm({
      title: t('message.confirmUserDeletion', {
        userName: member.name,
      }),
      content: t('message.operationCannotBeCancelled'),
      cancelText: t('cancel'),
      okText: t('actions.delete'),
      okType: 'danger',
      centered: true,
      maskClosable: true,
      onOk: (): void => {
        const messageKey = 'remove-member-message';
        message.loading({
          key: messageKey,
          content: t('message.deletingMember'),
          duration: 0,
        });

        removeMember(member.user_id)
          .then((): void => {
            message.success({
              key: messageKey,
              content: t('message.memberDeleted'),
              duration: 4,
            });
          })
          .catch((): void => {
            message.error({
              key: messageKey,
              content: t('message.failedToDeleteMember'),
              duration: 4,
            });
          });
      },
    });
  };

  return (
    <Spin size="large" spinning={isLoading}>
      <Table
        dataSource={members}
        pagination={{
          pageSize: 10,
        }}
        rowKey="user_id"
        columns={[
          {
            title: t('memberName'),
            dataIndex: 'name',
          },
          {
            title: t('emailAddress'),
            dataIndex: 'email',
          },
          {
            title: t('role'),
            render: (_: unknown, record: Member): ReactNode => {
              if (record.is_admin) {
                return <Tag color="pink">{t('roleAdmin')}</Tag>;
              }
              return <Tag color="blue">{t('roleMember')}</Tag>;
            },
          },
          {
            title: t('action'),
            render: (_: unknown, record: Member): ReactNode => {
              return (
                <Space>
                  <Button
                    size="small"
                    onClick={(): void => {
                      onSelect(record);
                    }}
                  >
                    {t('actions.edit')}
                  </Button>
                  <Button
                    danger
                    size="small"
                    disabled={record.user_id === currentUser?.userId}
                    onClick={(): void => {
                      handleClickRemoveMember(record);
                    }}
                  >
                    {t('actions.delete')}
                  </Button>
                </Space>
              );
            },
          },
        ]}
      />
    </Spin>
  );
};

export { MemberListTable };
